import React from "react";
import { authorizedFetch, authorizedRealmFetch } from "./boot.js";
import { deferNotificationPrompt, notificationPromptDeferred } from "./notification_prompt_session.js";
import { Card } from "./ui.jsx";

async function request(path, options = {}, requestRealm = null) {
  const response = requestRealm
    ? await authorizedRealmFetch(path, requestRealm, options)
    : await authorizedFetch(path, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
  return body;
}

const valuesFrom = data => ({
  dailyDigest: data?.preferences?.dailyDigest === true,
  closingSummary: data?.preferences?.closingSummary === true,
  xrayViewAlert: data?.preferences?.xrayViewAlert === true,
  scopeViewAlert: data?.preferences?.scopeViewAlert === true,
});

// The `@2` contract still carries `xrayViewAlert` and `scopeViewAlert`; they are read
// and written back unchanged, never shown. Viewing emails are the Repository's own
// (DECISIONS.md, 23 Sep 2026), so a toggle here would promise a letter this host
// does not write.
const preferenceCache = new Map();
const preferenceKey = (vendor, requestRealm) => `${requestRealm || window.VD?.access?.realm || "selected"}:${vendor}`;

async function readPreferences(path, vendor, requestRealm, fresh = false) {
  const key = preferenceKey(vendor, requestRealm);
  if (fresh) preferenceCache.delete(key);
  if (!preferenceCache.has(key)) {
    const pending = request(path, {}, requestRealm).catch(error => {
      preferenceCache.delete(key);
      throw error;
    });
    preferenceCache.set(key, pending);
  }
  return preferenceCache.get(key);
}

function preferencesChanged(vendor, requestRealm, source) {
  const key = preferenceKey(vendor, requestRealm);
  preferenceCache.delete(key);
  window.dispatchEvent(new CustomEvent("vd:notification-preferences-changed", {
    detail: { key, source },
  }));
}

export function NotificationPreferences({ vendor = window.VD.access.realm, requestRealm = null }) {
  const [data, setData] = React.useState(null);
  const [draft, setDraft] = React.useState({
    dailyDigest: false, closingSummary: false, xrayViewAlert: false, scopeViewAlert: false });
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [refreshRequired, setRefreshRequired] = React.useState(false);
  const [retry, setRetry] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const mounted = React.useRef(true);
  const instanceId = React.useRef(window.crypto.randomUUID());
  const readPath = `/collections/vendor.notifications.preferences@2/?${new URLSearchParams({ vendor })}`;

  const load = React.useCallback(async (fresh = false) => {
    const value = await readPreferences(readPath, vendor, requestRealm, fresh);
    if (!mounted.current) return;
    setData(value);
    setDraft(valuesFrom(value));
    setRefreshRequired(false);
    setRetry(null);
  }, [readPath, requestRealm, vendor]);

  React.useEffect(() => {
    mounted.current = true;
    let active = true;
    load().catch(() => {
      if (active) setError("Email preferences could not be loaded. Refresh to try again.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; mounted.current = false; };
  }, [load]);

  React.useEffect(() => {
    const listen = event => {
      if (event.detail?.key === preferenceKey(vendor, requestRealm)
        && event.detail?.source !== instanceId.current) load(true).catch(() => {});
    };
    window.addEventListener("vd:notification-preferences-changed", listen);
    return () => window.removeEventListener("vd:notification-preferences-changed", listen);
  }, [load, requestRealm, vendor]);

  async function refresh() {
    if (busy) return;
    setLoading(true); setError(""); setNotice("");
    try { await load(true); }
    catch (_) { if (mounted.current) setError("Email preferences could not be loaded. Refresh to try again."); }
    finally { if (mounted.current) setLoading(false); }
  }

  async function save(attempt = null, dismissPrompt = false) {
    if (busy || !data?.linked || data.canManage !== true) return;
    const values = dismissPrompt ? valuesFrom(data) : draft;
    const next = attempt || {
      input: {
        daily_digest: values.dailyDigest,
        closing_summary: values.closingSummary,
        xray_view_alert: values.xrayViewAlert,
        scope_view_alert: values.scopeViewAlert,
        expected_revision: data.revision,
        dismiss_prompt: dismissPrompt,
      },
      key: window.crypto.randomUUID(),
      dismissed: dismissPrompt,
    };
    setBusy(true); setError(""); setNotice("Saving email preferences…");
    try {
      await request("/actions/vendor.notifications.preferences.update@2/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: next.input, idempotency_key: next.key }),
      }, requestRealm);
      if (!mounted.current) return;
      setRetry(null); setRefreshRequired(true);
      try {
        preferencesChanged(vendor, requestRealm, instanceId.current);
        await load(true);
        if (mounted.current) setNotice(next.dismissed ? "You can choose email updates later in Profile." : "Email preferences saved.");
      } catch (_) {
        if (mounted.current) setError("The preferences were saved, but the current settings could not be loaded. Refresh before changing them again.");
      }
    } catch (failure) {
      if (!mounted.current) return;
      setNotice("");
      if ([400, 404, 409, 412].includes(failure.status)) {
        setRetry(null); setRefreshRequired(true);
        setError(failure.status === 409 || failure.status === 412
          ? "These preferences changed somewhere else. Refresh before saving again."
          : "These preferences could not be saved. Refresh and review them before trying again.");
      } else {
        setRetry(next);
        setError("The save could not be confirmed. Retry the same save before making another change.");
      }
    } finally { if (mounted.current) setBusy(false); }
  }

  const changed = data?.linked && (draft.dailyDigest !== data.preferences.dailyDigest
    || draft.closingSummary !== data.preferences.closingSummary
    || draft.xrayViewAlert !== data.preferences.xrayViewAlert
    || draft.scopeViewAlert !== data.preferences.scopeViewAlert);
  const locked = loading || busy || refreshRequired || Boolean(retry) || data?.canManage !== true;
  const role = data?.role === "owner" ? "Owner" : data?.role === "staff" ? "Staff" : data?.role;

  return <Card title="Email preferences" subtitle="Automated emails for your dashboard account"
    className="notification-preferences">
    {loading && <p role="status">Loading email preferences…</p>}
    {error && <p role="alert" className="error-text">{error}</p>}
    {!loading && !data && <button type="button" className="btn sm" onClick={refresh}>Refresh</button>}
    {data?.linked === false && <>
      <p className="muted">This account is not linked to a vendor person record. An Owner can invite the matching lot contact from People.</p>
      <div className="notification-options" aria-label="Email preferences unavailable">
        <label>
          <input type="checkbox" checked={false} disabled/>
          <span><strong>Daily viewing digest</strong><small>Daily viewing totals with the cumulative roll-up for this vendor.</small></span>
        </label>
        <label>
          <input type="checkbox" checked={false} disabled/>
          <span><strong>Sale closing summary</strong><small>One summary after the sale&apos;s last session.</small></span>
        </label>
      </div>
      <p className="caption">Email choices become available after this dashboard account is linked to a vendor person.</p>
    </>}
    {data?.linked && <>
      {data.promptRequired && <div className="notification-prompt" role="status">
        <strong>Choose your email updates</strong>
        <p>Use the recommended settings below, change them first, or come back later.</p>
        {data.canDismissPrompt && <button type="button" className="btn sm" disabled={locked}
          onClick={() => save(null, true)}>Not now</button>}
      </div>}
      <p className="caption">{data.contactName}{role ? ` · ${role}` : ""}</p>
      <div className="notification-options">
        <label>
          <input type="checkbox" checked={draft.dailyDigest} disabled={locked}
            onChange={event => { setDraft({ ...draft, dailyDigest: event.target.checked }); setNotice(""); }}/>
          <span><strong>Daily viewing digest</strong><small>Daily viewing totals with the cumulative roll-up for this vendor.</small></span>
        </label>
        <label>
          <input type="checkbox" checked={draft.closingSummary} disabled={locked}
            onChange={event => { setDraft({ ...draft, closingSummary: event.target.checked }); setNotice(""); }}/>
          <span><strong>Sale closing summary</strong><small>One summary after the sale&apos;s last session.</small></span>
        </label>
      </div>
      <p className="caption">These emails go only to dashboard people who can see Stats for this vendor. Lot contacts are never subscribed automatically.</p>
      <div className="notification-actions">
        {refreshRequired && <button type="button" className="btn sm" disabled={busy} onClick={refresh}>Refresh</button>}
        {retry && <button type="button" className="btn sm primary" disabled={busy} onClick={() => save(retry)}>Retry same save</button>}
        {!refreshRequired && !retry && <button type="button" className="btn sm primary"
          disabled={locked || (!changed && !data.promptRequired)} onClick={() => save()}>
          {data.promptRequired && !changed ? "Use recommended settings" : "Save preferences"}
        </button>}
      </div>
      {data.canManage === false && <p className="caption">These preferences are read-only for this account.</p>}
    </>}
    {notice && <p className="report-notice" role="status" aria-live="polite">{notice}</p>}
  </Card>;
}

export function NotificationPreferencePrompt({ vendor = window.VD.access.realm }) {
  const [data, setData] = React.useState(null);
  const [draft, setDraft] = React.useState({
    dailyDigest: false, closingSummary: false, xrayViewAlert: false, scopeViewAlert: false });
  const [busy, setBusy] = React.useState(false);
  const [retry, setRetry] = React.useState(null);
  const [refreshRequired, setRefreshRequired] = React.useState(false);
  const [error, setError] = React.useState("");
  const mounted = React.useRef(true);
  const instanceId = React.useRef(window.crypto.randomUUID());
  const dialogRef = React.useRef(null);
  const readPath = `/collections/vendor.notifications.preferences@2/?${new URLSearchParams({ vendor })}`;

  const load = React.useCallback(async (fresh = false) => {
    const value = await readPreferences(readPath, vendor, null, fresh);
    if (!mounted.current) return;
    setData(value); setDraft(valuesFrom(value)); setRetry(null); setRefreshRequired(false); setError("");
  }, [readPath, vendor]);

  React.useEffect(() => {
    mounted.current = true;
    load().catch(() => {});
    return () => { mounted.current = false; };
  }, [load]);

  React.useEffect(() => {
    const listen = event => {
      if (event.detail?.key === preferenceKey(vendor, null)
        && event.detail?.source !== instanceId.current) load(true).catch(() => {});
    };
    window.addEventListener("vd:notification-preferences-changed", listen);
    return () => window.removeEventListener("vd:notification-preferences-changed", listen);
  }, [load, vendor]);

  const showing = Boolean(data?.linked && data.promptRequired
    && !notificationPromptDeferred(vendor, data.contactId));
  React.useEffect(() => {
    if (!showing) return undefined;
    const previous = document.activeElement;
    const focusDialog = () => {
      const first = dialogRef.current?.querySelector("button:not(:disabled), input:not(:disabled)");
      (first || dialogRef.current)?.focus();
    };
    const frame = window.requestAnimationFrame
      ? window.requestAnimationFrame(focusDialog) : window.setTimeout(focusDialog, 0);
    return () => {
      if (window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      else window.clearTimeout(frame);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [showing]);

  async function act(attempt = null, dismiss = false) {
    if (busy || data?.canManage !== true) return;
    const values = dismiss ? valuesFrom(data) : draft;
    const next = attempt || {
      input: {
        daily_digest: values.dailyDigest,
        closing_summary: values.closingSummary,
        xray_view_alert: values.xrayViewAlert,
        scope_view_alert: values.scopeViewAlert,
        expected_revision: data.revision,
        dismiss_prompt: dismiss,
      },
      key: window.crypto.randomUUID(),
    };
    setBusy(true); setError("");
    try {
      await request("/actions/vendor.notifications.preferences.update@2/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: next.input, idempotency_key: next.key }),
      });
      if (!mounted.current) return;
      if (next.input.dismiss_prompt) deferNotificationPrompt(vendor, data.contactId);
      preferencesChanged(vendor, null, instanceId.current);
      await load(true);
    } catch (failure) {
      if (!mounted.current) return;
      if ([400, 404, 409, 412].includes(failure.status)) {
        setRetry(null);
        setRefreshRequired(true);
        setError("These preferences changed somewhere else. Refresh and choose again.");
      } else {
        setRetry(next);
        setError("The save could not be confirmed. Retry the same request.");
      }
    } finally { if (mounted.current) setBusy(false); }
  }

  if (!showing) return null;
  const changed = draft.dailyDigest !== data.preferences.dailyDigest
    || draft.closingSummary !== data.preferences.closingSummary;
  const locked = busy || Boolean(retry) || refreshRequired || data.canManage !== true;

  function keepFocus(event) {
    if (event.key === "Escape" && data.canDismissPrompt && !locked) {
      event.preventDefault(); act(null, true); return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...(dialogRef.current?.querySelectorAll("button:not(:disabled), input:not(:disabled)") || [])];
    if (!focusable.length) { event.preventDefault(); dialogRef.current?.focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  return <div className="notification-prompt-wrap" role="presentation">
    <section ref={dialogRef} className="notification-prompt-dialog" role="dialog" aria-modal="true"
      tabIndex="-1" onKeyDown={keepFocus}
      aria-labelledby="notification-prompt-title">
      <span className="eyebrow">Your email updates</span>
      <h2 id="notification-prompt-title">Choose what you want to receive</h2>
      <p>These settings apply to {data.vendorName || vendor}. You can change them later in Profile.</p>
      {error && <p className="error-text" role="alert">{error}</p>}
      <div className="notification-options">
        <label><input type="checkbox" checked={draft.dailyDigest} disabled={locked}
          onChange={event => setDraft({ ...draft, dailyDigest: event.target.checked })}/>
          <span><strong>Daily viewing digest</strong><small>Daily viewing totals and the cumulative roll-up.</small></span>
        </label>
        <label><input type="checkbox" checked={draft.closingSummary} disabled={locked}
          onChange={event => setDraft({ ...draft, closingSummary: event.target.checked })}/>
          <span><strong>Sale closing summary</strong><small>One summary after the sale&apos;s last session.</small></span>
        </label>
      </div>
      <div className="notification-prompt-actions">
        {refreshRequired
          ? <button type="button" className="btn primary" disabled={busy}
              onClick={() => load(true).catch(() => setError("Email preferences could not be refreshed."))}>Refresh settings</button>
          : retry
          ? <button type="button" className="btn primary" disabled={busy} onClick={() => act(retry)}>Retry same save</button>
          : <button type="button" className="btn primary" disabled={locked} onClick={() => act()}>
              {changed ? "Save my choices" : "Use recommended settings"}
            </button>}
        {!retry && !refreshRequired && data.canDismissPrompt && <button type="button" className="btn" disabled={locked}
          onClick={() => act(null, true)}>Not now</button>}
      </div>
      {data.promptSkips === 1 && <p className="caption">This is the final reminder. Profile will keep these settings available.</p>}
    </section>
  </div>;
}
